import angular from "angular";

export default function (
    $mdSidenav,
    SB_DETAILS_OVERLAY_ID,
    $q,
    $log,
    $timeout,
    $rootScope,
    $state,
    Analytics,
    EVENTS
) {
    "ngInject";

    const DEFAULT_TAB = 0;
    const DEFAULT_MODE = "full";
    const MAX_STACK_DEPTH = 10;
    const VALID_VIEW_TYPES = [
        "activity",
        "activities",
        "deliverable",
        "checklist-viewer",
        "checklist-filler",
        "structure",
    ];
    /**
     * Delay broadcasting a overlay state change event for x seconds if the new state is "closed"
     * @type {number}
     */
    const CACHE_OVERLAY_CONTENT_MS = 5000;

    let currentId = undefined;
    let currentSelectedTab = DEFAULT_TAB;
    let currentViewType = undefined;
    let currentMode = DEFAULT_MODE;
    let cacheTimer = undefined;
    let navigationStack = [];

    _broadcastNavigationState();

    $rootScope.$on(
        EVENTS.DELIVERABLE_OVERLAY_SELECTED_TAB_DID_CHANGE,
        function ($event, state) {
            if (currentViewType === "deliverable") {
                const selectedTab = state.currentSelectedTab;
                if (currentSelectedTab !== selectedTab) {
                    _updateTopEntryMetadata({ selectedTab });
                    _broadcastAndSaveOverlayState(currentId, "deliverable", {
                        selectedTab,
                        mode: currentMode,
                    });
                }
            }
        }
    );

    /**
     * always saves the current opened view and the selected tab in the state params
     */
    $rootScope.$on(
        EVENTS.DETAILS_OVERLAY_STATE_CHANGED,
        function ($event, { current = {} }) {
            const navigationOptions = {
                notify: false,
                location: "replace",
            };
            const params = {
                open: undefined,
                selectedTab: undefined,
            };

            const isTransientChecklistOverlay =
                current.viewType === "checklist-filler" ||
                current.viewType === "checklist-viewer";

            if (isTransientChecklistOverlay) {
                return;
            }

            if (current.id && current.viewType) {
                params.open = `${current.viewType}:${current.id}`;
                params.selectedTab = current.selectedTab || undefined;
            }
            $state.go($state.$current.name, params, navigationOptions);
        }
    );

    $rootScope.$on(
        EVENTS.DETAILS_OVERLAY_STATE_CHANGED,
        function ($event, { current = {}, previous = {} }) {
            if (!current.id || current.id === previous.id) {
                return;
            }
            if (current.viewType === "activity") {
                Analytics.trackEvent(
                    "sb_details_overlay",
                    "click",
                    "open activity details overlay"
                );
            } else if (current.viewType === "deliverable") {
                Analytics.trackEvent(
                    "Details Overlay",
                    "Open",
                    "from " + $state.current.name
                );
            }
        }
    );

    /**
     * opens the overlay if it finds an id and view type in the given state params and
     * selects the tab defined in the params
     * @param {{open: string, selectedTab: number}} stateParams - url state params
     * @returns {Promise}
     */
    function openFromStateParamsIfNeeded(stateParams) {
        if (!angular.isString(stateParams.open)) {
            return $q.reject();
        }

        const transitionMetadata = {
            dataContext: {
                projectId: stateParams.projectId,
            },
        };

        const [type, id] = stateParams.open.split(":");
        if (!id) {
            // fallback to classic where open is just an id
            return toggleView(
                "deliverable",
                stateParams.open,
                stateParams.selectedTab,
                DEFAULT_MODE,
                transitionMetadata
            );
        }
        return toggleView(
            type,
            id,
            stateParams.selectedTab,
            DEFAULT_MODE,
            transitionMetadata
        );
    }

    /**
     * if the overlay is open and shows the given view, the overlay is closed.
     * Otherwise, the overlay is opened for the request view and id
     *
     * @param {String} viewType      - One of deliverable | activity | structure
     * @param {String} id            - The id of the deliverable | activity | checklist to show information of.
     * @param {Number} [selectedTab] - Selected tab id in the overlay(default to ACTIVITIES)
     * @param {String} [mode]        - If the overlay should be shown in narrow mode
     * @return {Promise}             - Resolved when the sidenav has finished opening or has finished closing
     *
     * @deprecated use "toggle" instead, because it provides a more flexible interface and allows to pass additional options
     */
    function toggleView(
        viewType,
        id,
        selectedTab,
        mode = "full",
        transitionMetadata = {}
    ) {
        return toggle(viewType, {
            id,
            selectedTab,
            mode,
            ...transitionMetadata,
        });
    }

    function toggle(viewType, options = {}) {
        const transitionOptions = {
            ...options,
        };
        const normalizedViewType = _normalizeViewType(viewType);

        // make a meaningful identifier for the view that is opened
        // -> if there is an id - use it
        // -> if there are options - use a hash of the options
        // -> if there is nothing - use a timestamp to make sure it is always unique
        //
        let id = transitionOptions.id;
        if (!id) {
            id =
                Object.keys(transitionOptions).length > 0
                    ? JSON.stringify(transitionOptions)
                    : String(Date.now());
        }

        if (isOpen() && currentId === id && !transitionOptions.forcePush) {
            return close();
        }

        if (VALID_VIEW_TYPES.indexOf(normalizedViewType) === -1) {
            $log.error("Invalid viewType", viewType);
            _trackNavigationEvent("blocked_intent", "invalid_view_type");
            return close();
        }

        const mode = transitionOptions.mode || DEFAULT_MODE;

        if ([DEFAULT_MODE, "narrow"].indexOf(mode) === -1) {
            $log.error("Invalid mode", mode);
            _trackNavigationEvent("blocked_intent", "invalid_mode");
            return close();
        }

        const entry = _createEntry(id, normalizedViewType, {
            ...transitionOptions,
            mode,
        });

        if (_isForwardBlocked(entry)) {
            _trackNavigationEvent("blocked_intent", "qa_forward_blocked");
            return $q.reject("QA_FORWARD_BLOCKED");
        }

        if (_isConsecutiveDuplicate(entry)) {
            _trackNavigationEvent("blocked_intent", "consecutive_dedupe");
            return _open(entry.id, entry.viewType, {
                ...entry,
                selectedTab: entry.selectedTab,
                mode: entry.mode,
            });
        }

        _pushEntry(entry);
        return _open(entry.id, entry.viewType, {
            ...entry,
            selectedTab: entry.selectedTab,
            mode: entry.mode,
        });
    }

    function canGoBack() {
        return navigationStack.length > 1;
    }

    function getNavigationState() {
        return {
            canGoBack: canGoBack(),
            stackDepth: navigationStack.length,
            currentId,
            currentViewType,
            currentMode,
        };
    }

    function goBack() {
        if (!canGoBack()) {
            _trackNavigationEvent("back_click", "close");
            return close();
        }

        navigationStack.pop();
        _broadcastNavigationState();
        const previousEntry = navigationStack[navigationStack.length - 1];

        if (!previousEntry) {
            _trackNavigationEvent("blocked_intent", "missing_previous");
            return close();
        }

        _trackNavigationEvent("back_click", String(navigationStack.length));
        return _open(previousEntry.id, previousEntry.viewType, {
            ...previousEntry,
            selectedTab: previousEntry.selectedTab,
            mode: previousEntry.mode,
        });
    }

    function resetNavigationState() {
        _resetNavigationStack();
    }

    function _normalizeViewType(viewType) {
        if (viewType === "checklist") {
            return "checklist-viewer";
        }

        return viewType;
    }

    function _createEntry(id, viewType, options) {
        const mode = options.mode || DEFAULT_MODE;
        const shouldReuseCurrentDeliverableTab =
            viewType === "deliverable" &&
            currentViewType === "deliverable" &&
            !angular.isNumber(options.selectedTab);
        const selectedTab = shouldReuseCurrentDeliverableTab
            ? currentSelectedTab
            : options.selectedTab;

        return {
            id,
            viewType,
            selectedTab: angular.isNumber(selectedTab)
                ? selectedTab
                : DEFAULT_TAB,
            mode,
            dataContext: options.dataContext || {},
            contextKey:
                options.contextKey ||
                _buildContextKey(viewType, id, mode, options),
            key: `${viewType}:${id}:${mode}`,
            timestamp: Date.now(),
        };
    }

    function _buildContextKey(viewType, id, mode, options) {
        const dataContext = options.dataContext || {};
        const stateParams = ($state && $state.params) || {};
        const projectId =
            dataContext.projectId || stateParams.projectId || "unknown-project";

        return `${projectId}:${viewType}:${id}:${mode}`;
    }

    function _isConsecutiveDuplicate(entry) {
        const topEntry = navigationStack[navigationStack.length - 1];
        return Boolean(topEntry && topEntry.key === entry.key);
    }

    function _isForwardBlocked(/*entry*/) {
        return false;

        // TODO: instead of blocking we have to inform the user about a pending state that needs
        //   resolving before navigating away; Silently not changing the view will lead to confusion.
        //   -> so, after resolving the pending state, we should allow the user to continue with the navigation
        // const topEntry = navigationStack[navigationStack.length - 1];
        // if (!topEntry) {
        //     return false;
        // }
        //
        // if (topEntry.viewType !== "checklist-filler") {
        //     return false;
        // }
        //
        // return true;
    }

    function _pushEntry(entry) {
        navigationStack.push(entry);
        if (navigationStack.length > MAX_STACK_DEPTH) {
            navigationStack = navigationStack.slice(
                navigationStack.length - MAX_STACK_DEPTH
            );
        }

        _broadcastNavigationState();
    }

    function _updateTopEntryMetadata(patch) {
        const topEntry = navigationStack[navigationStack.length - 1];
        if (!topEntry) {
            return;
        }

        navigationStack[navigationStack.length - 1] = {
            ...topEntry,
            ...patch,
        };
    }

    function _resetNavigationStack() {
        navigationStack = [];
        _broadcastNavigationState();
    }

    function _broadcastNavigationState() {
        $rootScope.$broadcast(EVENTS.DETAILS_OVERLAY_NAVIGATION_STATE_CHANGED, {
            current: getNavigationState(),
        });
    }

    function _trackNavigationEvent(action, label) {
        Analytics.trackEvent("Details Overlay Navigation", action, label);
    }

    /**
     * open - Open the overlay
     *
     * @param {String} id            - The id of the deliverable | activity | checklist to show information of.
     * @param {String} viewType      - One of deliverable | activity | structure
     * @param {Object} [options]             - Additional properties for the overlay
     * @param {Number} [options.selectedTab] - Selected tab id in the overlay(default to ACTIVITIES)
     * @param {String} [options.mode]        - If the overlay should be shown in narrow mode
     * @param {Object} [options.dataContext] - Any data that the view might need
     * @return {Promise}             - Resolved when the sidenav has finished opening
     */
    function _open(id, viewType, options) {
        // stop a potential clear cache event to be emitted
        //
        if (cacheTimer) {
            $timeout.cancel(cacheTimer);
            cacheTimer = undefined;
        }

        // emit that a new state is requested to be shown in the overlay
        //
        _broadcastAndSaveOverlayState(id, viewType, options);

        return $mdSidenav(SB_DETAILS_OVERLAY_ID).open();
    }

    function _broadcastAndSaveOverlayState(id, viewType, options) {
        const previousId = currentId;
        const previousSelectedTab = currentSelectedTab;
        const previousViewType = currentViewType;
        const previousMode = currentMode;
        currentId = id;
        currentSelectedTab = options.selectedTab || DEFAULT_TAB;
        currentViewType = viewType;
        currentMode = options.mode || DEFAULT_MODE;
        $rootScope.$broadcast(EVENTS.DETAILS_OVERLAY_STATE_CHANGED, {
            previous: {
                id: previousId,
                viewType: previousViewType,
                selectedTab: previousSelectedTab,
                mode: previousMode,
            },
            current: {
                id,
                selectedTab: currentSelectedTab,
                viewType,
                mode: currentMode,
                dataContext: options.dataContext || {},
            },
        });
    }

    /**
     * close - Close the overlay
     *
     * @return {Promise}              - Resolved when the sidenav has finished closing
     */
    function close() {
        if (cacheTimer) {
            $timeout.cancel(cacheTimer);
            cacheTimer = undefined;
        }

        cacheTimer = $timeout(() => {
            _broadcastAndSaveOverlayState(undefined, undefined, {
                selectedTab: 0,
            });
        }, CACHE_OVERLAY_CONTENT_MS);

        $rootScope.$broadcast(EVENTS.DETAILS_OVERLAY_CLOSED);

        return $mdSidenav(SB_DETAILS_OVERLAY_ID)
            .close()
            .then(function onOverlayClosed() {
                _resetNavigationStack();
                _trackNavigationEvent("reset_triggered", "close");
            });
    }

    /**
     * isOpen - Checks if the overlay is open
     *
     * @return {Boolean}              - Resolved when the sidenav has finished closing
     */
    function isOpen() {
        return $mdSidenav(SB_DETAILS_OVERLAY_ID).isOpen();
    }

    function onClose(callback) {
        return $mdSidenav(SB_DETAILS_OVERLAY_ID).onClose(function () {
            if (angular.isFunction(callback)) {
                callback();
            }
        });
    }

    return {
        openFromStateParamsIfNeeded: openFromStateParamsIfNeeded,
        // @deprecated use "toggle" instead because it provides a more flexible interface and allows to pass additional options
        toggleView: toggleView,
        toggle: toggle,
        close: close,
        isOpen: isOpen,
        onClose: onClose,
        _currentStateForTest: () => ({
            currentId,
            currentSelectedTab,
            currentViewType,
            currentMode,
            canGoBack: canGoBack(),
            stackDepth: navigationStack.length,
            stackEntries: navigationStack,
        }),
        canGoBack: canGoBack,
        getNavigationState: getNavigationState,
        goBack: goBack,
        resetNavigationState: resetNavigationState,
    };
}
